import {
  CONSENT_COOKIE_MAX_AGE_SEC,
  CONSENT_COOKIE_NAME,
  CONSENT_POLICY_VERSION
} from '../constants'
import type { ConsentChoice } from '../types'

/**
 * The consent choice lives in a first-party cookie (FE-330): it must outlive
 * the tab, and a cookie — unlike localStorage — can later be read by an edge
 * or server that serves the same site. Every accessor is guarded; blocked
 * cookies only mean the banner shows again.
 */
const isConsentChoice = (value: unknown): value is ConsentChoice =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as ConsentChoice).analytics === 'boolean' &&
  typeof (value as ConsentChoice).marketing === 'boolean' &&
  typeof (value as ConsentChoice).version === 'string' &&
  typeof (value as ConsentChoice).grantedAt === 'string'

const readRawCookie = (name: string, cookie: string) => {
  const prefix = `${name}=`
  const entry = cookie
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith(prefix))

  return entry ? entry.slice(prefix.length) : null
}

/** Returns the stored choice, or `null` when absent, broken or outdated. */
export const readConsentCookie = (
  cookie = safeDocumentCookie()
): ConsentChoice | null => {
  const raw = readRawCookie(CONSENT_COOKIE_NAME, cookie)
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw))

    return (
        isConsentChoice(parsed) && parsed.version === CONSENT_POLICY_VERSION
      ) ?
        parsed
      : null
  } catch {
    return null
  }
}

export const writeConsentCookie = (choice: ConsentChoice) => {
  const value = encodeURIComponent(JSON.stringify(choice))
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  try {
    document.cookie = `${CONSENT_COOKIE_NAME}=${value}; Max-Age=${CONSENT_COOKIE_MAX_AGE_SEC}; Path=/; SameSite=Lax${secure}`
  } catch {
    // NOTE: cookies blocked — the choice still applies to this page load
  }
}

export const clearConsentCookie = () => {
  try {
    document.cookie = `${CONSENT_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`
  } catch {
    // NOTE: nothing to clear when cookies are unavailable
  }
}

function safeDocumentCookie() {
  try {
    return typeof document === 'undefined' ? '' : document.cookie
  } catch {
    return ''
  }
}
